import { memo, useMemo, useState } from 'react';
import { useHistory } from 'react-router-dom';
import {
  Button,
  Chip,
  IconButton,
  Link,
  List,
  ListItem,
  ListItemSecondaryAction,
  Menu,
  MenuItem,
  Typography,
} from '@mui/material';
import { makeStyles } from '@mui/styles';
import {
  DataGridPremium as DataGrid,
  GridColDef,
  GridFilterPanel,
  GridRenderCellParams,
  GridToolbarContainer,
  GridToolbarFilterButton,
  GridValueGetterParams,
} from '@mui/x-data-grid-premium';
import { format } from 'date-fns';
import { EquifaxTranscriptStatus, Estate, EstateStatus, EstateType } from 'queries';
import { FileText, MoreVertical } from 'ts-react-feather-icons';

import { NodeEnvironment } from 'utils/enum';
import {
  canClearResultsCache,
  canPullResults,
  canPurgeResults,
  EstateStatusLabel,
} from 'utils/estate';

type Props = {
  estates: Estate[];
  // eslint-disable-next-line @typescript-eslint/ban-types
  handleImpersonateView: Function;
  // eslint-disable-next-line @typescript-eslint/ban-types
  handleDocumentsVerified: Function;
  // eslint-disable-next-line @typescript-eslint/ban-types
  handleSendConsent: Function;
  // eslint-disable-next-line @typescript-eslint/ban-types
  handleMarkConsentReceived: Function;
  // eslint-disable-next-line @typescript-eslint/ban-types
  handleClearCache: Function;
  // eslint-disable-next-line @typescript-eslint/ban-types
  handlePurgeEstate: Function;
  // eslint-disable-next-line @typescript-eslint/ban-types
  handlePullReportData: Function;
  // eslint-disable-next-line @typescript-eslint/ban-types
  handleChangeStatus: Function;
  // eslint-disable-next-line @typescript-eslint/ban-types
  // handleRequestTaxTranscriptsClick: Function;
  // eslint-disable-next-line @typescript-eslint/ban-types
  handleStatusCheckTaxTranscriptsClick: Function;
  // eslint-disable-next-line @typescript-eslint/ban-types
  handleRetrieveTaxTranscriptsClick: Function;
};

const useStyles = makeStyles(() => ({
  root: {
    // no border around the cells when focused
    '&.MuiDataGrid-root .MuiDataGrid-cell, &.MuiDataGrid-root .MuiDataGrid-columnHeader': {
      '&:focus, &:focus-within': {
        outline: 'none',
      },
    },
  },
  estateContainer: {
    marginTop: '0px',
    width: '100%',
    background: '#FFFFFF',
    justifyContent: 'center',
    boxShadow: '0px 2px 5px rgba(0, 0, 0, 0.25)',
    borderRadius: '2px',
    overflow: 'auto',
  },
  success: {
    color: 'green',
  },
  nameWrapper: {
    display: 'block',
    lineHeight: '1.43',
  },
  menuHeader: {
    paddingLeft: 5,
    cursor: 'default',
  },
  secondaryLabel: {
    display: 'block',
    lineHeight: '1.43',
    fontSize: '.75rem',
    color: '#999',
  },
  listAction: {
    height: '100%',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
  },
  estateType: {
    verticalAlign: 'super',
    fontSize: '.55rem',
  },
  link: {
    paddingTop: '4px',
    paddingRight: '10px',
    cursor: 'pointer',
  },
  filterPanelWrapper: {
    position: 'absolute',
    top: '-60px',
    backgroundColor: '#F1FCFF',
  },
}));

const EstateActionsGrid = ({
  estates,
  handleImpersonateView,
  handleDocumentsVerified,
  handleSendConsent,
  handleMarkConsentReceived,
  handleClearCache,
  handlePurgeEstate,
  handlePullReportData,
  handleChangeStatus,
  // handleRequestTaxTranscriptsClick,
  handleStatusCheckTaxTranscriptsClick,
  handleRetrieveTaxTranscriptsClick,
}: Props) => {
  const classes = useStyles();
  const history = useHistory();

  const [currentEstate, setCurrentEstate] = useState<Estate | null>(null);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const handleMenuClick = () => {
    setAnchorEl(null);
  };

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const handleActionClick = (event: any) => {
    setAnchorEl(event.currentTarget);
  };

  const PortalCellMemoized = useMemo(() => {
    const Cell = ({ row }: GridRenderCellParams) => {
      const estate = row as Estate;

      return (
        <Link
          className={classes.link}
          onClick={() => {
            handleImpersonateView({
              type: 'portal',
              portalId: estate.portal!.id,
            });
            return false;
          }}
        >
          {estate.portal!.name}
        </Link>
      );
    };

    // Only re-render the cell component if the `value` prop changes
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const areEqual = (prevProps: any, nextProps: any) => prevProps.value === nextProps.value;

    return memo(Cell, areEqual);
  }, [classes.link, handleImpersonateView]);

  const EstateNameCellMemoized = useMemo(() => {
    const Cell = ({ row }: GridRenderCellParams) => {
      const estate = row as Estate;

      const estateTypeCaption =
        estate.type === EstateType.Truadmin
          ? 'Adm'
          : estate.type === EstateType.Truplanning
            ? 'Pln'
            : estate.type === EstateType.Truproxy
              ? 'Pxy'
              : estate.type === EstateType.Trustadmin
                ? 'TAdm'
                : '';
      const clientLabel = estate.estateClientFirstName + ' ' + estate.estateClientLastName;
      return (
        <div>
          <div className={classes.nameWrapper}>
            <div>
              <Link
                className={classes.link}
                onClick={() => {
                  handleImpersonateView({
                    type: 'estate',
                    portalId: estate.portal!.id,
                    estateId: estate.id,
                  });
                  return false;
                }}
              >
                {estate.estateName}
              </Link>
              <Typography color="primary" className={classes.estateType} variant="caption">
                {estateTypeCaption}
              </Typography>
            </div>
            <div className={classes.secondaryLabel}>Client: {clientLabel}</div>
          </div>
        </div>
      );
    };

    // Only re-render the cell component if the `value` prop changes
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const areEqual = (prevProps: any, nextProps: any) => prevProps.value === nextProps.value;

    return memo(Cell, areEqual);
  }, [
    classes.estateType,
    classes.link,
    classes.nameWrapper,
    classes.secondaryLabel,
    handleImpersonateView,
  ]);

  const StatusCellMemoized = useMemo(() => {
    const Cell = ({ row }: GridRenderCellParams) => {
      const estate = row as Estate;

      let ccrCachedOn = '';
      let propertyCachedOn = '';
      let twnCachedOn = '';
      if (estate.ccrDate) {
        ccrCachedOn = format(Date.parse(estate.ccrDate.toString()), 'LLL d y');
      }
      if (estate.pvDate) {
        propertyCachedOn = format(Date.parse(estate.pvDate.toString()), 'LLL d y');
      }
      // estate.twnDate logs any response from the EFX, request and status responses
      if (estate.twnDate) {
        twnCachedOn = format(Date.parse(estate.twnDate.toString()), 'LLL d y');
      }
      return (
        <div>
          <div className={classes.nameWrapper}>
            <div>
              {EstateStatusLabel[estate.status].adminLabel ||
                EstateStatusLabel[estate.status].label}
            </div>
            {ccrCachedOn ? (
              <div className={classes.secondaryLabel}>Cached ccr: {ccrCachedOn}</div>
            ) : (
              ''
            )}
            {propertyCachedOn ? (
              <div className={classes.secondaryLabel}>Cached property: {propertyCachedOn}</div>
            ) : (
              ''
            )}
            {twnCachedOn ? (
              <div className={classes.secondaryLabel}>Cached transcripts: {twnCachedOn}</div>
            ) : (
              ''
            )}
          </div>
        </div>
      );
    };

    // Only re-render the cell component if the `value` prop changes
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const areEqual = (prevProps: any, nextProps: any) => prevProps.value === nextProps.value;

    return memo(Cell, areEqual);
  }, [classes.nameWrapper, classes.secondaryLabel]);

  const DocumentsVerifiedCellMemoized = memo((estate: Estate) => {
    const verified = estate.documentsVerified;
    const readyForVerify =
      estate.status === EstateStatus.TrustateApproval || EstateStatus.ResultsReady;

    return (
      <div>
        <Link
          className={classes.link}
          onClick={() => {
            handleImpersonateView({
              type: 'documents',
              estateId: estate.id,
              portalId: estate.portal!.id,
              documentsFolderId: estate.documentsFolderId,
            });
          }}
        >
          <FileText />
        </Link>
        {verified ? (
          <Chip className={classes.success} size="small" label="Verified" variant="outlined" />
        ) : (
          <Button
            onClick={() => {
              handleDocumentsVerified(estate.id);
            }}
            color="secondary"
            variant="contained"
            size="small"
            disabled={!readyForVerify}
          >
            mark verified
          </Button>
        )}
      </div>
    );
  });

  const ClientConsentReceivedCellMemoized = useMemo(() => {
    const Cell = ({ row, value }: GridRenderCellParams) => {
      const estate = row as Estate;
      const consented = value as boolean;

      const sendLabel =
        estate.status === EstateStatus.ClientConsentEmailSent ? 'resend email' : 'send email';
      const consentedOn = estate.clientConsentDate
        ? format(Date.parse(estate.clientConsentDate.toString()), 'LLL d y')
        : '';
      const readyForConsent =
        estate.status === EstateStatus.ClientConsent ||
        estate.status === EstateStatus.ClientConsentEmailSent;
      return (
        <div>
          {consented ? (
            <div className={classes.nameWrapper}>
              <div>
                <Chip
                  className={classes.success}
                  size="small"
                  label="Received"
                  variant="outlined"
                />
              </div>
              {consentedOn ? <div className={classes.secondaryLabel}>{consentedOn}</div> : ''}
            </div>
          ) : (
            <Button
              onClick={() => {
                handleSendConsent(estate.id, estate.portal!.id, estate.type);
              }}
              color="secondary"
              variant="contained"
              size="small"
              disabled={!readyForConsent}
            >
              {sendLabel}
            </Button>
          )}
        </div>
      );
    };

    // Only re-render the cell component if the `value` prop changes
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const areEqual = (prevProps: any, nextProps: any) => prevProps.value === nextProps.value;

    return memo(Cell, areEqual);
  }, [classes.nameWrapper, classes.secondaryLabel, classes.success, handleSendConsent]);

  const LoadResultsCellMemoized = useMemo(() => {
    const Cell = ({ row }: GridRenderCellParams) => {
      const estate = row as Estate;

      const resultsReady = estate.ccrDate || estate.pvDate;
      const hasResults = estate.results!.length > 0;
      const { canPull, reason: canPullReason } = canPullResults(
        estate.ccrDate,
        estate.pvDate,
        estate.clientConsentDate,
        estate.documentsVerified,
        hasResults,
      );
      return (
        <div>
          {resultsReady && hasResults ? (
            <Chip className={classes.success} size="small" label="Ready" variant="outlined" />
          ) : (
            <div className={classes.nameWrapper}>
              <div>
                <Button
                  onClick={() => {
                    handlePullReportData(estate.id);
                  }}
                  color="secondary"
                  variant="contained"
                  size="small"
                  disabled={!canPull}
                >
                  Load Results
                </Button>
              </div>
              {canPullReason ? <div className={classes.secondaryLabel}>({canPullReason})</div> : ''}
            </div>
          )}
        </div>
      );
    };

    // Only re-render the cell component if the `value` prop changes
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const areEqual = (prevProps: any, nextProps: any) => prevProps.value === nextProps.value;

    return memo(Cell, areEqual);
  }, [classes.nameWrapper, classes.secondaryLabel, classes.success, handlePullReportData]);

  const UpdatedCellMemoized = useMemo(() => {
    const Cell = ({ row }: GridRenderCellParams) => {
      const estate = row as Estate;

      const updated = estate.updatedAt;
      const date: string = format(Date.parse(updated!.toString()), 'LLL d y');
      const time: string = format(Date.parse(updated!.toString()), 'h:mm bbb');

      return (
        <div>
          <div className={classes.nameWrapper}>
            <div>
              <Typography variant="caption">{date}</Typography>
            </div>
            <div className={classes.nameWrapper}>
              <Typography variant="caption">{time}</Typography>
            </div>
          </div>
        </div>
      );
    };

    // Only re-render the cell component if the `value` prop changes
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const areEqual = (prevProps: any, nextProps: any) => prevProps.value === nextProps.value;

    return memo(Cell, areEqual);
  }, [classes.nameWrapper]);

  const EstateMenuCellMemoized = useMemo(() => {
    const Cell = ({ row }: GridRenderCellParams) => {
      const estate = row as Estate;

      return (
        <List>
          <ListItem alignItems="center">
            <ListItemSecondaryAction>
              <IconButton
                id={estate.id}
                onClick={(e) => {
                  setCurrentEstate(estate);
                  handleActionClick(e);
                }}
                aria-controls={estate.id}
                size="large"
              >
                <MoreVertical />
              </IconButton>
            </ListItemSecondaryAction>
          </ListItem>
        </List>
      );
    };

    // Only re-render the cell component if the `value` prop changes
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const areEqual = (prevProps: any, nextProps: any) => prevProps.value === nextProps.value;

    return memo(Cell, areEqual);
  }, []);

  const EstateMenuMemoized = memo((estate: Estate) => {
    const hasResults = estate.results!.length > 0;
    const { canClear, reason: canClearReason } = canClearResultsCache(
      estate.ccrDate,
      estate.clientConsentDate,
    );
    const { canPurge, reason: canPurgeReason } = canPurgeResults(estate.ccrDate, hasResults);

    return (
      <Menu
        open={anchorEl?.id === estate.id}
        anchorEl={anchorEl}
        keepMounted
        onClose={() => handleMenuClick()}
      >
        <MenuItem className={classes.menuHeader}>Estate Actions</MenuItem>
        <MenuItem
          key="view tasks"
          onClick={() => {
            history.push(`/admin/estates/${estate.id}/tasks`);
          }}
        >
          View tasks
        </MenuItem>
        <MenuItem
          key="edit items"
          onClick={() => {
            history.push(`/admin/estates/${estate.id}/result-items`);
          }}
          disabled={!estate.ccrDate && !estate.pvDate}
        >
          View result items
        </MenuItem>
        <MenuItem
          key="mark consent received"
          onClick={() => {
            handleMenuClick();
            handleMarkConsentReceived(estate.id, estate.portal!.id, estate.type);
          }}
          disabled={estate.clientConsentReceived}
        >
          Mark consent received
        </MenuItem>
        <MenuItem
          key={estate.status === EstateStatus.Completed ? 'mark in process' : 'mark completed'}
          onClick={() => {
            handleMenuClick();
            handleChangeStatus(
              estate.status === EstateStatus.Completed
                ? EstateStatus.TrustateApproval
                : EstateStatus.Completed,
              estate,
              estate.portal!.id,
            );
          }}
        >
          {estate.status === EstateStatus.Completed
            ? 'Change status: In Process'
            : 'Change status: Completed'}
        </MenuItem>
        <MenuItem className={classes.menuHeader}>Equifax Credit & Property</MenuItem>
        <MenuItem
          key="purge estate"
          onClick={() => {
            handleMenuClick();
            handlePurgeEstate(estate.id, estate.portal!.id);
          }}
          disabled={!canPurge}
        >
          Purge data
          {canPurgeReason && ` (${canPurgeReason})`}
        </MenuItem>
        {(estate.type === EstateType.Truadmin || estate.type === EstateType.Trustadmin) && (
          <div>
            <MenuItem className={classes.menuHeader}>Equifax Tax</MenuItem>
            {/* Removed because we're not signing 4506C in app at this time */}
            {/* <MenuItem
              key="request tax transcripts"
              onClick={() => {
                handleMenuClick();
                handleRequestTaxTranscriptsClick(estate);
              }}
              disabled={estate.results![0]?.twnServerId ? true : false}
            >
              Request Tax Transcripts
            </MenuItem> */}
            <MenuItem
              key="tax transcripts status"
              onClick={() => {
                handleMenuClick();
                handleStatusCheckTaxTranscriptsClick(estate);
              }}
              disabled={
                !estate.results![0]?.twnServerId ||
                estate.results![0]?.twnStatus === EquifaxTranscriptStatus.Complete
              }
            >
              Check Tax Transcripts Status: {`${estate.results![0]?.twnStatus}`}
            </MenuItem>
            <MenuItem
              key="retrieve tax transcripts"
              onClick={() => {
                handleMenuClick();
                handleRetrieveTaxTranscriptsClick(estate);
              }}
              disabled={
                estate.results![0]?.twnResponseDate
                  ? true
                  : false || estate.results![0]?.twnStatus !== EquifaxTranscriptStatus.Complete
              }
            >
              Retrieve Tax Transcripts
            </MenuItem>
          </div>
        )}
        {process.env.REACT_APP_NODE_ENV === NodeEnvironment.DEV && (
          <div>
            <MenuItem className={classes.menuHeader}>Dev Environment Only</MenuItem>
            <MenuItem
              key="clear results cache"
              onClick={() => {
                handleMenuClick();
                handleClearCache(estate.id, estate.portal!.id);
              }}
              disabled={!canClear}
            >
              Clear results cache
              {canClearReason && ` (${canClearReason})`}
            </MenuItem>
          </div>
        )}
      </Menu>
    );
  });

  const columns: GridColDef[] = [
    {
      disableColumnMenu: true,
      field: 'portal',
      filterable: true,
      flex: 6,
      headerName: 'Portal',
      renderCell: (params: GridRenderCellParams) => <PortalCellMemoized {...params} />,
      valueGetter: (params: GridValueGetterParams) => params.row.portal.name,
    },
    {
      disableColumnMenu: true,
      field: 'estateName',
      flex: 6,
      headerName: 'Estate Name',
      renderCell: (params: GridRenderCellParams) => <EstateNameCellMemoized {...params} />,
      valueGetter: (params: GridValueGetterParams) => params.row.estateName,
    },
    {
      disableColumnMenu: true,
      field: 'status',
      filterable: true,
      flex: 6,
      headerName: 'Status',
      renderCell: (params: GridRenderCellParams) => <StatusCellMemoized {...params} />,
      valueGetter: (params: GridValueGetterParams) =>
        EstateStatusLabel[params.row.status as EstateStatus].adminLabel ||
        EstateStatusLabel[params.row.status as EstateStatus].label,
    },
    {
      disableColumnMenu: true,
      field: 'documentsVerified',
      filterable: false,
      flex: 5,
      headerName: 'Docs Verified',
      renderCell: (params: GridRenderCellParams) => {
        const estate = params.row as Estate;
        return <DocumentsVerifiedCellMemoized {...estate} />;
      },
      valueGetter: (params: GridValueGetterParams) => {
        return params.row.documentsVerified;
      },
    },
    {
      disableColumnMenu: true,
      field: 'clientConsentReceived',
      filterable: false,
      flex: 4,
      headerName: 'Consent',
      renderCell: (params: GridRenderCellParams) => (
        <ClientConsentReceivedCellMemoized {...params} />
      ),
      valueGetter: (params: GridValueGetterParams) => {
        return params.row.clientConsentReceived;
      },
    },
    {
      disableColumnMenu: true,
      field: 'loadResults',
      filterable: false,
      flex: 4,
      headerName: 'Results',
      renderCell: (params: GridRenderCellParams) => <LoadResultsCellMemoized {...params} />,
      valueGetter: (params: GridValueGetterParams) => {
        return params.row.clientConsentReceived;
      },
    },
    {
      disableColumnMenu: true,
      field: 'updated',
      filterable: false,
      flex: 5,
      headerName: 'Updated',
      renderCell: (params: GridRenderCellParams) => <UpdatedCellMemoized {...params} />,
      valueGetter: (params: GridValueGetterParams) => {
        return params.row.updatedAt;
      },
    },
    {
      disableColumnMenu: true,
      field: 'estateMenu',
      filterable: false,
      flex: 2,
      headerName: '',
      renderCell: (params: GridRenderCellParams) => <EstateMenuCellMemoized {...params} />,
    },
  ];

  function CustomToolbar() {
    return (
      <GridToolbarContainer>
        <GridToolbarFilterButton
          {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            ...({} as any)
          }
        />
      </GridToolbarContainer>
    );
  }

  function CustomFilterPanel() {
    return (
      <div className={classes.filterPanelWrapper}>
        <GridFilterPanel />
      </div>
    );
  }

  return (
    <div className={classes.estateContainer}>
      {currentEstate && <EstateMenuMemoized {...currentEstate} />}
      <div style={{ width: '100%' }}>
        <DataGrid
          className={classes.root}
          columns={columns}
          rows={estates}
          disableColumnReorder
          disableRowSelectionOnClick
          autoHeight={true}
          rowHeight={65}
          pagination
          initialState={{
            pagination: { paginationModel: { pageSize: 100 } },
          }}
          components={{
            Toolbar: CustomToolbar,
            FilterPanel: CustomFilterPanel,
          }}
        />
      </div>
    </div>
  );
};

export default memo(EstateActionsGrid);
